"use client";

import type React from "react";
import { useEffect } from "react";

import { usePathname, useRouter } from "next/navigation";

import { useAuth } from "@/lib/use-auth";
import { isFamilyRole, isPatientRole } from "@/lib/utils/user-roles";
import { UserRole } from "@/types/user-roles";

import { CompactGlobalSidebar } from "./compact-global-sidebar";
import { ShellLoadingSkeleton } from "./loading-skeleton";

// Navigation items with role permissions (for role checks)
const navigationItems = [
  {
    name: "Patients",
    href: "/patients",
    roles: [UserRole.ADMIN, UserRole.PROVIDER],
  },
  // {
  //   name: "Orders & Results",
  //   href: "/orders-results",
  //   roles: [UserRole.ADMIN, UserRole.PROVIDER],
  // },
  {
    name: "Scheduling",
    href: "/scheduling",
    roles: [UserRole.ADMIN, UserRole.PROVIDER],
  },
  { name: "Dashboard", href: "/dashboard", roles: [UserRole.ADMIN] },
  { name: "User Management", href: "/user-management", roles: [UserRole.ADMIN] },
  { name: "Admin", href: "/admin", roles: [UserRole.ADMIN] },
];

const patientNavigation = [{ name: "Patient Portal", href: "/patient" }];
const familyNavigation = [{ name: "Family Portal", href: "/family-portal" }];

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user, isLoading, isAuthenticated, signOut } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  // Filter navigation items based on user role
  const getNavigationForRole = (userRole: UserRole) => {
    if (isPatientRole(userRole)) return patientNavigation;
    if (isFamilyRole(userRole)) return familyNavigation;
    return navigationItems.filter((item) => item.roles.includes(userRole));
  };

  // Skip all auth checks for demo pages and onboarding pages
  const isDemoPage = pathname.startsWith("/demo");
  const isOnboardingPage = pathname.startsWith("/onboarding");

  // NOTE: Auth redirect removed - middleware handles authentication redirects
  // This eliminates duplicate auth checks that added 50-100ms latency.
  // Trust middleware as single source of truth for auth.

  // Role-based access restrictions
  useEffect(() => {
    if (isDemoPage || isOnboardingPage) return;

    // Never redirect when on auth flows (forgot-password, reset-password)
    if (pathname.startsWith("/auth")) return;

    if (
      !user?.role ||
      pathname.startsWith("/login") ||
      pathname.startsWith("/patient-portal") ||
      pathname.startsWith("/patient") ||
      pathname.startsWith("/family-portal") ||
      pathname.startsWith("/admin") // Admin pages handle their own access control
    ) {
      return;
    }

    const currentNavigation = getNavigationForRole(user.role);
    const allowedPaths = currentNavigation.map((item) => item.href);

    // Check if current path is allowed for this role
    const isPathAllowed = allowedPaths.some((path) => pathname.startsWith(path));

    if (!isPathAllowed) {
      const firstAllowedPath = allowedPaths[0] || "/patients";
      router.push(firstAllowedPath);
    }
  }, [user?.role, pathname, router, isDemoPage, isOnboardingPage]);

  // Show shell with skeleton while auth loads - gives immediate visual feedback
  if (isLoading) {
    return <ShellLoadingSkeleton />;
  }

  // Show login page, auth flows, and certain portals without shell
  // Auth routes (forgot-password, reset-password) must never show sidebar - security
  if (
    !isAuthenticated ||
    pathname === "/login" ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/patient-portal") ||
    pathname.startsWith("/family-portal") ||
    pathname.startsWith("/due-diligence")
  ) {
    return <>{children}</>;
  }

  return (
    <div className="relative min-h-screen bg-[hsl(210_25%_98%)]">
      <CompactGlobalSidebar user={user} onSignOut={signOut} />

      <div className="relative lg:pl-16">
        <div className="h-14 lg:hidden" />

        <main className="min-h-[calc(100vh-3.5rem)] lg:min-h-screen">
          <div key={pathname} className="animate-fade-in-up px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
